import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { USER_OPERATIONS, RESOURCE_TYPE, MESSAGE_TYPE } from "../../constants";
import styles from "./LineCapacity.module.css";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import CapacityForm from "../CapacityForm/CapacityForm";
import { getLineLimitsPerPlant, updateLineLimits } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";

const LineCapacity = ({ setHasActiveEdits, handleCountChange }) => {
  const ROW_HEIGHT = 38;

  const RESOURCE_OPERATION = USER_OPERATIONS.UPDATE_MASTER_LIMTS; 

  const { showError, showInternalError, showPopup } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [recordCount, setRecordCount] = useState(0);

  const [showForm, setShowForm] = useState(false);

  const [selectedLine, setSelectedLine] = useState(null);

  const setCount = (count) => {
    setRecordCount(count);
  };

  const fixedColumns = [
    {
      width: 140,
      title: "Line Id",
      field: "line",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Line Name",
      field: "lineName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Max Capacity",
      field: "maxCapacity",
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);

  const getLineCapacityData = async () => {
    if (!plant.value) {
      return;
    }

    setRowsData((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      const response = await getLineLimitsPerPlant(plant.value);

      setRowsData({
        loading: false,
        data: response.data,
        filteredData: response.data,
      });
    } catch (err) {
      showInternalError({ error: err });
    }
  };

  const openCapacityForm = (selectedRows) => {
    if (!selectedRows.length) {
      showError({
        contextText: "No Row Selected",
        info: "Please select one line.",
      });
      return;
    }

    if (selectedRows.length > 1) {
      showError({
        contextText: "Multiple Rows Selected",
        info: "Only one line can be updated at a time.",
      });
      return;
    }

    setSelectedLine(selectedRows[0]);
    setShowForm(true);
  };

  const closeFormPopup = () => {
    setShowForm(false);
    setSelectedLine(null);
  };

  const updateCapacity = async (newCapacity) => {
    try {
      const payload = [
        {
          id: selectedLine.id,
          newCapacity: Number(newCapacity),
        },
      ];

      await updateLineLimits(payload);

      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Capacity updated successfully.",
        info: `${selectedLine.lineName} capacity updated to ${newCapacity}.`,
      });

      closeFormPopup();
      getLineCapacityData();
    } catch (error) {
      showError({
        contextText: "Update Failed",
        info: "Unable to update line capacity.",
      });
    }
  };

  useEffect(() => {
    handleCountChange(recordCount);
  }, [recordCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    if (plant.value) {
      getLineCapacityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value]);

  return (
    <>
      <div className={styles.rgtTools}>
        <div className={styles.lineCapacityToolbar}>
          <div className={styles.plantName}>Selected Plant - {plant.label}</div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            primaryAction={{
              name: "UPDATE CAPACITY",
              actionFn: openCapacityForm,
              authOperation: RESOURCE_OPERATION,
            }}
          />
        </div>
      </div>

      {showForm && selectedLine && (
        <CapacityForm
          selectedRecord={{
            ...selectedLine,
            name: selectedLine.lineName,
          }}
          recordLabel="Line Name"
          formTitle="Line Capacity Configurator"
          closeFormPopup={closeFormPopup}
          updateCapacity={updateCapacity}
        />
      )}
    </>
  );
};

LineCapacity.propTypes = {
  setHasActiveEdits: PropTypes.func.isRequired,
  handleCountChange: PropTypes.func,
};

export default withAllowedOperationsProvider(
  LineCapacity,
  false,
  RESOURCE_TYPE.MASTER_CAPACITY, 
);
